import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronDown, ChevronRight, Bell, BellOff } from 'lucide-react';
import { supabase } from '../supabaseClient';

const JurySummonsButton = ({ trial, user, onSummonsSent, buttonLabel = null, fullWidth = true, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [crewsWithMembers, setCrewsWithMembers] = useState([]);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [expandedCrews, setExpandedCrews] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const hasHydratedForOpenRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const userId = user?.id || null;

  const COST_PER_SUMMON = 10; // Fixed cost

  // ✅ Support both trial.trialMode (from your TrialDetail) and trial.trial_mode (if you ever pass DB row directly)
  const isDecideForMe =
    trial?.trialMode === 'decide_for_me' ||
    trial?.trial_mode === 'decide_for_me';

  const summonButtonLabel = buttonLabel || (isDecideForMe ? '🤔 Ask Your Crew' : '⚖️ Summon Your Crew');

  // Notification copy
  const notificationTitle = isDecideForMe ? '🤔 Decide for me!' : '⚖️ Jury Summons!';
  const notificationBody = isDecideForMe
    ? `${user?.username || 'Someone'} wants your call before the verdict lands. Vote in the next 5 min for +150 coins.`
    : `${user?.username || 'Someone'} wants your take before this closes. Vote in the next 5 min for +150 coins.`;

  // Preview copy
  const previewLine = isDecideForMe
    ? `🤔 ${user?.username || 'Someone'} needs your help deciding!`
    : `⚖️ ${user?.username || 'Someone'} summoned you to vote!`;

  const fetchCrewsAndMembers = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      console.log('[summon-ui] picker_hydrate_start', { actor_user_id: userId });
      const { data: userCrews, error: crewError } = await supabase
        .from('crew_members')
        .select('crew_id, crews(id, name, emoji)')
        .eq('user_id', userId);

      if (crewError) throw crewError;

      if (!userCrews || userCrews.length === 0) {
        setCrewsWithMembers([]);
        setLoading(false);
        return;
      }

      const uniqueCrews = [];
      const seenCrewIds = new Set();

      userCrews.forEach(uc => {
        if (uc.crews && !seenCrewIds.has(uc.crews.id)) {
          seenCrewIds.add(uc.crews.id);
          uniqueCrews.push(uc.crews);
        }
      });

      const crewsData = await Promise.all(
        uniqueCrews.map(async (crew) => {
          const { data: crewMembers, error: membersError } = await supabase
            .from('crew_members')
            .select('user_id')
            .eq('crew_id', crew.id)
            .neq('user_id', userId);

          if (membersError || !crewMembers || crewMembers.length === 0) {
            return { ...crew, members: [] };
          }

          const memberIds = crewMembers.map(cm => cm.user_id);

          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('id, username, fcm_token')
            .in('id', memberIds);

          if (usersError) {
            return { ...crew, members: [] };
          }

          const formattedMembers = (usersData || []).map(u => ({
            id: u.id,
            username: u.username,
            hasNotifications: !!u.fcm_token
          }));

          return {
            id: crew.id,
            name: crew.name,
            emoji: crew.emoji,
            members: formattedMembers
          };
        })
      );

      setCrewsWithMembers(crewsData);

      const firstCrewWithMembers = crewsData.find(c => c.members.length > 0);
      if (firstCrewWithMembers) {
        setExpandedCrews(new Set([firstCrewWithMembers.id]));
      }
      console.log('[summon-ui] picker_hydrate_end', {
        actor_user_id: userId,
        crew_count: crewsData.length,
      });
    } catch (error) {
      console.error('[summon-ui] picker_hydrate_error', {
        actor_user_id: userId,
        error: error?.message || String(error),
      });
      setCrewsWithMembers([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!isOpen) {
      hasHydratedForOpenRef.current = false;
      return;
    }
    if (!userId) return;
    if (hasHydratedForOpenRef.current) return;
    hasHydratedForOpenRef.current = true;
    fetchCrewsAndMembers();
  }, [isOpen, userId, fetchCrewsAndMembers]);

  const toggleFriend = (friendId) => {
    setSelectedFriends(prev => {
      if (prev.includes(friendId)) {
        return prev.filter(id => id !== friendId);
      } else {
        return [...prev, friendId];
      }
    });
  };

  const toggleCrewExpanded = (crewId) => {
    setExpandedCrews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(crewId)) {
        newSet.delete(crewId);
      } else {
        newSet.add(crewId);
      }
      return newSet;
    });
  };

  const selectAllInCrew = (e, members) => {
    e.stopPropagation();

    const allIds = members.map(m => m.id);

    setSelectedFriends(prev => {
      const currentSet = new Set(prev);
      allIds.forEach(id => currentSet.add(id));
      return Array.from(currentSet);
    });
  };

  const deselectAllInCrew = (e, members) => {
    e.stopPropagation();

    const memberIds = members.map(m => m.id);
    setSelectedFriends(prev => prev.filter(id => !memberIds.includes(id)));
  };

  const isCrewFullySelected = (members) => {
    if (members.length === 0) return false;
    return members.every(m => selectedFriends.includes(m.id));
  };

  const getSelectedCountInCrew = (members) => {
    return members.filter(m => selectedFriends.includes(m.id)).length;
  };

  const getNotNotifiedCount = () => {
    const allMembers = crewsWithMembers.flatMap(c => c.members);
    return selectedFriends.filter(id => {
      const member = allMembers.find(m => m.id === id);
      return member && !member.hasNotifications;
    }).length;
  };

  const sendSummons = async () => {
    if (selectedFriends.length === 0 || sending || submitInFlightRef.current) return;

    submitInFlightRef.current = true;
    setSending(true);
    try {
      console.log('[summon-ui] summon_click', {
        trial_id: trial?.id,
        actor_user_id: userId,
        target_count: selectedFriends.length,
      });
      // Calculate total cost
      const totalCost = selectedFriends.length * COST_PER_SUMMON;

      // Check if user has enough coins
      const { data: currentUserData, error: coinsError } = await supabase
        .from('users')
        .select('coins')
        .eq('id', userId)
        .single();

      if (coinsError) throw coinsError;

      if (!currentUserData || currentUserData.coins < totalCost) {
        alert(`Not enough coins! You need ${totalCost} coins.`);
        return;
      }

      // Deduct coins
      const { error: updateError } = await supabase
        .from('users')
        .update({ coins: currentUserData.coins - totalCost })
        .eq('id', userId);

      if (updateError) throw updateError;

      // Send summons + push via server-owned function (single canonical path).
      try {
        if (selectedFriends.length > 0) {
          console.log('[summon-api] summon_request_sent', {
            trial_id: trial?.id,
            actor_user_id: userId,
            target_count: selectedFriends.length,
          });

          const { data: enqueueResult, error: enqueueError } = await supabase.functions.invoke(
            'send-summons-notifications',
            {
              body: {
                trial_id: trial.id,
                recipient_ids: selectedFriends,
                title: notificationTitle,
                body: notificationBody,
                data: {
                  type: 'jury_summons',
                  trial_mode: isDecideForMe ? 'decide_for_me' : 'debate',
                  trial_id: trial.id,
                  summoner_id: userId,
                },
                create_summons_rows: true,
                reason: 'jury_summons_button',
              },
            },
          );

          if (enqueueError) {
            const errorPayload = enqueueError?.context || null;
            console.error('[summon-api] summon_request_result', {
              trial_id: trial?.id,
              actor_user_id: userId,
              request_id: errorPayload?.request_id || null,
              build_id: errorPayload?.buildId || null,
              error: enqueueError.message || String(enqueueError),
              code: errorPayload?.code || enqueueError?.code || null,
              details: errorPayload?.details || null,
              hint: errorPayload?.hint || null,
            });
            throw enqueueError;
          }

          if (enqueueResult?.ok === false) {
            const serverError = enqueueResult?.error || 'Unknown summon enqueue error';
            console.error('[summon-api] summon_request_result', {
              trial_id: trial?.id,
              actor_user_id: userId,
              request_id: enqueueResult?.request_id || null,
              build_id: enqueueResult?.buildId || null,
              error: serverError,
              code: enqueueResult?.code || null,
              details: enqueueResult?.details || null,
              hint: enqueueResult?.hint || null,
            });
            throw new Error(serverError);
          }

          console.log('[summon-api] summon_request_result', {
            trial_id: trial?.id,
            actor_user_id: userId,
            target_count: selectedFriends.length,
            request_id: enqueueResult?.request_id || null,
            build_id: enqueueResult?.buildId || null,
            summon_rows_inserted: enqueueResult?.totals?.summons_rows_inserted ?? 0,
            success_count: enqueueResult?.totals?.enqueued ?? 0,
            failure_count: enqueueResult?.totals?.failed ?? 0,
          });
          console.log('[summon-queue] summon_push_jobs_enqueued', {
            trial_id: trial?.id,
            actor_user_id: userId,
            target_count: selectedFriends.length,
            enqueued_count: enqueueResult?.totals?.enqueued ?? 0,
            sent_count: 0,
            skipped_duplicate: enqueueResult?.totals?.already_queued ?? 0,
            skipped_self: enqueueResult?.totals?.skipped_self ?? 0,
            failed_count: enqueueResult?.totals?.failed ?? 0,
          });
        }
      } catch (notifyError) {
        console.warn('⚠️ Summons saved but push notifications failed:', notifyError);
      }

      const notNotifiedCount = getNotNotifiedCount();

      let message = `✅ Sent to ${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''}!`;
      if (notNotifiedCount > 0) {
        message += `\n\n⚠️ ${notNotifiedCount} won't receive push notifications (they'll see it when they log in)`;
      }

      alert(message);

      if (onSummonsSent) {
        onSummonsSent(selectedFriends.length);
      }

      setIsOpen(false);
      setSelectedFriends([]);
    } catch (error) {
      const errorMessage = error?.message || String(error || '');
      console.error('[summon-ui] summon_submit_error', {
        trial_id: trial?.id,
        actor_user_id: userId,
        error: errorMessage,
      });
      alert(errorMessage || 'Error sending summons. Please try again.');
    } finally {
      setSending(false);
      submitInFlightRef.current = false;
    }
  };

  const totalCost = selectedFriends.length * COST_PER_SUMMON;
  const notNotifiedCount = getNotNotifiedCount();

  return (
    <>
      <button
        onClick={() => {
          console.log('[summon-ui] picker_open', { trial_id: trial?.id, actor_user_id: userId });
          setIsOpen(true);
        }}
        className={`${fullWidth ? 'w-full' : 'w-auto'} px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl font-bold hover:from-purple-700 hover:to-purple-800 transition-all transform hover:scale-105 shadow-lg ${className}`.trim()}
      >
        {summonButtonLabel}
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto border-2 border-purple-500 shadow-2xl relative">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X size={24} />
            </button>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-black text-white mb-2">
                {isDecideForMe ? '🤔 Ask Your Crew' : '⚖️ Summon Your Crew'}
              </h2>
              <p className="text-gray-300 text-sm">"{trial.title}"</p>
              <p className="text-purple-300 text-xs mt-1">{COST_PER_SUMMON} coins per person</p>
            </div>

            <div className="mb-6">
              <label className="text-white font-bold text-sm mb-2 block">
                Select Friends:
              </label>

              {loading ? (
                <div className="text-center py-8 text-gray-400">
                  Loading crew members...
                </div>
              ) : crewsWithMembers.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-400 mb-3">No crew members yet!</div>
                  <div className="text-sm text-gray-500">Create a crew first</div>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {crewsWithMembers.map(crew => {
                    const members = crew.members || [];
                    const totalCount = members.length;
                    const selectedInCrew = getSelectedCountInCrew(members);
                    const isExpanded = expandedCrews.has(crew.id);
                    const isFullySelected = isCrewFullySelected(members);

                    return (
                      <div key={crew.id} className="bg-gray-700 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between p-3">
                          <button
                            type="button"
                            onClick={() => toggleCrewExpanded(crew.id)}
                            className="flex items-center gap-2 flex-1 text-left hover:bg-gray-600 rounded p-1 transition-colors"
                          >
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            <span className="text-xl">{crew.emoji}</span>
                            <div>
                              <div className="text-white font-bold">{crew.name}</div>
                              <div className="text-xs text-gray-400">
                                {selectedInCrew > 0 ? `${selectedInCrew}/` : ''}{totalCount} member{totalCount !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </button>

                          {totalCount > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                if (isFullySelected) {
                                  deselectAllInCrew(e, members);
                                } else {
                                  selectAllInCrew(e, members);
                                }
                              }}
                              className={`px-3 py-1 rounded text-xs font-bold transition-all ${
                                isFullySelected
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                              }`}
                            >
                              {isFullySelected ? '✓ All' : 'Select All'}
                            </button>
                          )}
                        </div>

                        {isExpanded && (
                          <div className="space-y-1 p-2 bg-gray-800">
                            {members.length === 0 ? (
                              <div className="text-center py-4 text-gray-400 text-sm">
                                No other members yet
                              </div>
                            ) : (
                              members.map(friend => (
                                <button
                                  key={friend.id}
                                  type="button"
                                  onClick={() => toggleFriend(friend.id)}
                                  className={`w-full flex items-center justify-between p-2 rounded-lg transition-all ${
                                    selectedFriends.includes(friend.id)
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                  }`}
                                >
                                  <span className="text-sm font-medium">{friend.username}</span>
                                  <div className="flex items-center gap-2">
                                    {!friend.hasNotifications ? (
                                      <BellOff size={14} className="text-gray-400" title="Won't receive push notification" />
                                    ) : (
                                      <Bell size={14} className="text-green-400" title="Will receive push notification" />
                                    )}
                                    {selectedFriends.includes(friend.id) && (
                                      <span className="text-white">✓</span>
                                    )}
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedFriends.length > 0 && (
              <div className="mb-4 p-3 bg-gray-700 rounded-lg">
                <div className="text-white text-sm font-bold mb-2">Preview:</div>
                <div className="text-gray-300 text-sm">
                  {previewLine}
                </div>
                <div className="text-gray-400 text-xs mt-1">"{trial.title}"</div>

                {notNotifiedCount > 0 && (
                  <div className="mt-2 text-xs text-yellow-400 flex items-center gap-1">
                    <BellOff size={12} />
                    {notNotifiedCount} won't get push notification{notNotifiedCount > 1 ? 's' : ''}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setIsOpen(false)}
                className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg font-bold hover:bg-gray-600 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={sendSummons}
                disabled={selectedFriends.length === 0 || sending}
                className={`flex-1 px-4 py-3 rounded-lg font-bold transition-all ${
                  selectedFriends.length === 0 || sending
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800'
                }`}
              >
                {sending ? 'Sending...' : `Send (${selectedFriends.length}) - ${totalCost} 🪙`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JurySummonsButton;
